import React from "react";
import {
  getAshButtonState,
  getAshMessage,
  getAshStatus,
  I9ButtonState,
  I9Type,
  MessageYouMustCompleteSection1,
} from "./i9-data";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { ErrorCode, getErrorMessage } from "./i9-error-message";
import { I9Legacy } from "./i9-legacy";
import { I9Caas } from "./i9-caas";
import { Tag } from "@amzn/stencil-react-components/tag";
import { Row } from "@amzn/stencil-react-components/layout";
import useArb from "../../hooks/use-arb";
import { Text } from "../typography";
import { Link } from "@amzn/stencil-react-components/link";
import { getLocale, isEnglishLocale, isSpanishLocale } from "../../utility/locale-helper";
import { USCIS_URL, USCIS_URL_ES } from "../../config/urls";

const supportLinkUrl = window.location.origin + (isEnglishLocale() ? "/contact-us" : "/" + getLocale() + "/contact-us");
export interface I9Response {
  isRemote?: boolean;
  errorCode?: string;
  secureURL?: string;
  allowRetry?: boolean;
  status?: string;
}

interface I9ControlledProps {
  showStatus?: boolean;
  loading?: boolean;
  i9Response?: I9Response;
  i9ErrorCode?: ErrorCode;
  onRetry: () => void;
  onSubmit: () => void;
}

export const I9Controlled = ({
  showStatus,
  loading,
  i9Response,
  i9ErrorCode,
  onRetry,
  onSubmit,
}: I9ControlledProps) => {
  const bundle = useArb("HVHChecklistPantherResource");
  const here = bundle.getMessage("Checklist-MainPage-Link");
  const loadingText = bundle.getMessage("Checklist-MainPage-I9-loading");
  const supportLink = (
    <Link target="_blank" href={supportLinkUrl}>
      {here}
    </Link>
  );
  const uscis_link = isSpanishLocale() ? USCIS_URL_ES : USCIS_URL;
  const i9Link = (
    <Link target="_blank" href={uscis_link}>
      {here}
    </Link>
  );

  if (loading || !i9Response) {
    return <Spinner size={SpinnerSize.Small} loadingText={loadingText} showText={true}></Spinner>;
  }

  if (i9ErrorCode === ErrorCode.E422) {
    if (i9Response.secureURL) {
      return (
        <I9Caas
          message={MessageYouMustCompleteSection1}
          buttonState={I9ButtonState.Active}
          secureURL={i9Response.secureURL}
          onRetry={onRetry}
          onSubmit={onSubmit}
        />
      );
    }

    if (
      ["Section2Complete", "PendingSection3", "Section3Complete", "Archived"].includes(i9Response?.status || "") ===
      false
    ) {
      return <I9Legacy onLinkClick={onSubmit} />;
    } else {
      i9ErrorCode = undefined;
    }
  }

  const errorMessage = getErrorMessage(i9ErrorCode, bundle);

  const i9Type = i9Response.isRemote === true ? I9Type.Remote : I9Type.Normal;
  const i9Status = i9Response.status;
  let messageInText = undefined;
  if (i9Status && i9Type) {
    const messageAfterTranslation = bundle.formatMessage(getAshMessage(i9Type, i9Status), {
      i9Link: i9Link,
      supportLink: supportLink,
    });
    messageInText = <Text>{messageAfterTranslation}</Text>;
  }

  const message = i9Status && messageInText;
  const buttonState = i9Status !== undefined ? getAshButtonState(i9Type, i9Status) : undefined;
  const allowRetry = i9Response.allowRetry || i9ErrorCode === ErrorCode.Internal;
  const statusBeforeTranslation = i9Status && getAshStatus(i9Type, i9Status);
  const ashStatus = statusBeforeTranslation && bundle.getMessage(statusBeforeTranslation);

  return (
    <>
      {showStatus && ashStatus && (
        <Row margin="0 0 1rem 0">
          <Tag>{ashStatus}</Tag>
        </Row>
      )}
      <I9Caas
        errorMessage={errorMessage}
        message={message}
        buttonState={buttonState}
        secureURL={i9Response.secureURL}
        allowRetry={allowRetry}
        onRetry={onRetry}
        onSubmit={onSubmit}
      />
    </>
  );
};
